import React, { useState, useEffect, useRef } from "react";
import "./DemoResultsScreen.css";
import { Typography } from "@mui/material";
import InfoCards from "../../components/InfoCards/InfoCards";
import countIcn from "../../assets/countIcon.svg";
import PhonemeAccuracyIcn from "../../assets/phoneme_accuray_icn.svg";
import ResultCard from "../../components/ResultCard/ResultCard";
import correct from "../../assets/correct.svg";
import missed from "../../assets/missed.svg";
import extra from "../../assets/extra.svg";
import incorrect from "../../assets/incorrect.svg";
import DetailedResultCard from "../../components/DetailedResultCard/DetailedResultCard";
import correctPercentageIcn from "../../assets/correctPercentageIcn.svg";
import useTranslationSetup from "../../utils/useTranslationSetup";
import ArrowIcon from "../../assets/back_arrow.svg";
import StarSparkle from "../../assets/star_sparkle.svg";
import AudioPlayer from "../../components/audioPlayer/AudioPlayer";
import useParamValues from "../../utils/useParamValues";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import feedbackSpeakerIcn from "../../assets/feedback_speaker_icn.svg";
import feedbackUserIcn from "../../assets/feedback_user_icn.svg";
import feedbackMicrophoneIcn from "../../assets/feedback_microphone_icn.svg";
import feedbackWidgetIcn from "../../assets/feedback_widget.svg";
import feedbackWidgetDisabledIcn from "../../assets/feedback_widget_disabled.svg";
import CustomErrorDialogBox from "../../components/CustomErrorDialogBox/CustomErrorDialogBox";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import sendFeedback from "../../fetchApi/sendFeedback";
import { UrlConstants } from "../../utils/CommonUtils/constants";
import * as Sentry from "@sentry/react";
import { produceEventLog } from "../../utils/helper";
import { getUserProperties } from "../../utils/helper";
import SubjectTag from "../../components/subjectTag/SubjectTag";
import AudioQualityWarningBanner from "../../components/AudioQualityWarningBanner/AudioQualityWarningBanner";

const ASSESSMENT_VIEW_TYPE = {
  PREVIOUS: "previous",
  CURRENT: "current",
};

function DemoResultsScreen({
  processedData,
  goBack,
  feedbackGivenIds,
  markFeedbackGiven,
}) {
  const [expandText, setExpandText] = useState(false);
  const { t, tSubject } = useTranslationSetup();
  const {
    studentName,
    classNum,
    section,
    token: urlToken,
    assessmentLang,
  } = useParamValues();
  const scrollToRef = useRef(null);
  const [scrollCardIndex, setScrollCardIndex] = useState(null);
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const [feedbackDrawerOpen, setFeedbackDrawerOpen] = useState(false);
  const [feedbackValueSelected, setFeedbackValueSelected] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [showFeedbackErrorDialog, setShowFeedbackErrorDialog] = useState(false);
  const [feedbackResponses, setFeedbackResponses] = useState({});
  const [feedbackSubmitDisabled, setFeedbackSubmitDisabled] = useState(false);
  const [audioBlobRef, setAudioBlobRef] = useState(null);
  const [currentAssessmentType, setCurrentAssessmentType] = useState(
    ASSESSMENT_VIEW_TYPE.CURRENT,
  );
  const assessmentUrl = `${
    UrlConstants.BASE_URL + UrlConstants.BASE_PATH_URL + UrlConstants.ASSESSMENT
  }`;
  const userProperties = getUserProperties(useParamValues());
  const previousAssessmentData = processedData?.previous_result || null;
  const hasPreviousAssessment = Boolean(previousAssessmentData?.result);
  const isShowingPreviousAssessment =
    currentAssessmentType === ASSESSMENT_VIEW_TYPE.PREVIOUS &&
    hasPreviousAssessment;
  const activeProcessedData = isShowingPreviousAssessment
    ? previousAssessmentData
    : processedData;
  const activeResult = activeProcessedData?.result || {};

  useEffect(() => {
    if (scrollToRef.current) {
      scrollToRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [scrollCardIndex, scrollToRef, scrollTrigger]);

  useEffect(() => {
    if (
      currentAssessmentType === ASSESSMENT_VIEW_TYPE.PREVIOUS &&
      !hasPreviousAssessment
    ) {
      setCurrentAssessmentType(ASSESSMENT_VIEW_TYPE.CURRENT);
    }
  }, [currentAssessmentType, hasPreviousAssessment]);

  const handleFeedbackButtonClick = (value) => {
    if (!activeProcessedData?.feedback) {
      setFeedbackValueSelected(value);
      setFeedbackDrawerOpen(true);
    }
  };

  const handleBackClick = () => {
    goBack();
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === "feedback-bottom-drawer-overlay") {
      setFeedbackDrawerOpen(false);
      setFeedbackValueSelected(null);
      setFeedbackText("");
    }
  };

  const scrollToTop = () => {
    const topNav = document.getElementById("Top-Nav");
    if (topNav) {
      topNav.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const handleQuickSightCardOnClick = (i) => {
    let newIndex;
    switch (i) {
      case t("correct_words"):
        newIndex = 0;
        break;
      case t("missed_words"):
        newIndex = 1;
        break;
      case t("extra_words"):
        newIndex = 2;
        break;
      case t("incorrect_words"):
        newIndex = 3;
        break;
      default:
        newIndex = null;
    }

    setScrollCardIndex(newIndex);
    setScrollTrigger((prev) => prev + 1);
  };

  const handleAssessmentTypeSelection = (assessmentType) => {
    if (
      assessmentType === ASSESSMENT_VIEW_TYPE.PREVIOUS &&
      !hasPreviousAssessment
    ) {
      return;
    }
    setCurrentAssessmentType(assessmentType);
    setScrollCardIndex(null);
  };

  const detailedResult = [
    {
      index: 3,
      heading: t("incorrect_words"),
      subHeading: t("incorrect_words_detail"),
      bgColor: "rgba(212, 43, 83, 0.13)",
      innerBorderColor: "#D42B53",
      icon: incorrect,
      words: activeResult.incorrect_words || [],
    },
    {
      index: 0,
      heading: t("correct_words"),
      subHeading: t("correct_words_detail"),
      bgColor: "rgba(126, 182, 107, 0.17)",
      innerBorderColor: "#7EB66B",
      icon: correct,
      words: activeResult.correct_words || [],
    },
    {
      index: 1,
      heading: t("missed_words"),
      subHeading: t("missed_words_detail"),
      bgColor: "rgba(154, 23, 201, 0.07)",
      innerBorderColor: "#9A17C9",
      icon: missed,
      words: activeResult.missed_words || [],
    },
    ...(assessmentLang !== "en"
      ? [
          {
            index: 2,
            heading: t("extra_words"),
            subHeading: t("extra_words_detail"),
            bgColor: "rgba(95, 188, 240, 0.13)",
            innerBorderColor: "#5FBCF0",
            icon: extra,
            words: activeResult.extra_words || [],
          },
        ]
      : []),
  ];

  const formattedWordsPercentage = activeResult?.correct_percentage
    ? (activeResult.correct_percentage * 100).toFixed(0)
    : 0;

  const formattedPhonemePercentage = activeResult?.phoneme_accuracy
    ? (activeResult.phoneme_accuracy * 100).toFixed(0)
    : 0;

  const handleFeedbackSubmitButtonClick = async () => {
    const feedbackData = {
      resultMetExpectation: feedbackValueSelected,
      feedbackText: feedbackText,
    };
    const submittedId = activeProcessedData?.id;
    try {
      setFeedbackSubmitDisabled(true);
      const response = await sendFeedback(
        { feedback: feedbackData },
        `${assessmentUrl}/${submittedId}/feedback`,
        urlToken,
      );
      if (submittedId != null) {
        setFeedbackResponses((prev) => ({ ...prev, [submittedId]: response }));
      }
      if (response.status == "Ok") {
        setFeedbackDrawerOpen(false);
        setFeedbackValueSelected(null);
        setFeedbackText("");
        if (submittedId != null && typeof markFeedbackGiven === "function") {
          markFeedbackGiven(submittedId);
        }
      } else if (response.status === "ERROR") {
        setShowFeedbackErrorDialog(true);
        throw new Error(response.data);
      }
    } catch (error) {
      Sentry.captureException(error);
      setShowFeedbackErrorDialog(error);
    } finally {
      setFeedbackSubmitDisabled(false);
    }
    produceEventLog("result_2a_feedback_button", {
      ...userProperties,
    });
  };

  const handleFeedbackRetryClick = () => {
    setShowFeedbackErrorDialog(false);
  };

  const activeId = activeProcessedData?.id;
  const isFeedbackDisabled = !!(
    activeProcessedData?.feedback ||
    (activeId != null && feedbackResponses?.[activeId]) ||
    (activeId != null && feedbackGivenIds?.[activeId])
  );

  return (
    <div className="resultPage">
      <CustomErrorDialogBox
        header={t("something_went_wrong")}
        icon={
          <ErrorOutlineOutlinedIcon
            style={{
              color: "#D95959",
              borderRadius: "10px",
              marginTop: "16px",
              width: "80px",
              height: "80px",
            }}
          />
        }
        primaryButtonText={t("retake")}
        isDialogOpen={showFeedbackErrorDialog}
        showPrimaryButton
        onClickPrimaryButton={handleFeedbackRetryClick}
      />
      <div className="header-fixed-wrapper">
        <div className="back-arrow-div">
          <img
            src={ArrowIcon}
            alt="Back"
            width="24"
            height="24"
            className="back-arrow"
            onClick={handleBackClick}
          />
          {UrlConstants.SHOW_COMPARISON && (
            <div className="header-text-assessment-type custom-ff-regular">
              {isShowingPreviousAssessment
                ? `${t("previous")} ${t("assessment")}`
                : `${t("current")} ${t("assessment")}`}
            </div>
          )}
        </div>
        <div className="header">
          <div className="header-student-name-container">
            <div className="header-text custom-ff-regular">{studentName}</div>
            <div className="header-grade-text custom-ff-regular">
              {t("grade")} - {classNum}
              {section}
            </div>
          </div>
          {UrlConstants.SHOW_COMPARISON && hasPreviousAssessment && (
            <div className="header-assessment-type-selector-container">
              <div className="header-assessment-type-selector-item">
                <div className="header-assessment-type-selector-item-text custom-ff-regular">
                  {t("compare_previous_current")}
                </div>
                <div className="header-assessment-type-selector-button-container">
                  <div
                    className={`header-assessment-type-selector-button-item ${
                      isShowingPreviousAssessment ? "active" : ""
                    }`}
                    onClick={() =>
                      handleAssessmentTypeSelection(
                        ASSESSMENT_VIEW_TYPE.PREVIOUS,
                      )
                    }
                  >
                    <div className="header-assessment-type-selector-button-item-text">
                      {t("previous")}
                    </div>
                    <div className="header-assessment-type-selector-button-item-year">
                      2025
                    </div>
                  </div>
                  <div
                    className={`header-assessment-type-selector-button-item ${
                      !isShowingPreviousAssessment ? "active" : ""
                    }`}
                    onClick={() =>
                      handleAssessmentTypeSelection(
                        ASSESSMENT_VIEW_TYPE.CURRENT,
                      )
                    }
                  >
                    <div className="header-assessment-type-selector-button-item-text">
                      {t("current")}
                    </div>
                    <div className="header-assessment-type-selector-button-item-year">
                      2026
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="assessment-info-div">
        <div className="assessment-result-heading-row">
          <SubjectTag/>
          <Typography className="assessment-result-heading">
            {activeProcessedData?.assessment_type === "primary"
              ? t("assessment_primary")
              : t("assessment_secondary")}
          </Typography>
        </div>
        {/* <div className="ai-result-border-wrapper">
          <div className="ai-result-info-wrapper">
            <img alt="star sparkle" src={StarSparkle} />
            <div className="ai-result-info">
              <Typography className="ai-assessment-result-heading">
                {t("ai_assessment_result")}
              </Typography>
              <Typography className="ai-assessment-result-info">
                {t("ai_assessment_result_info")}
              </Typography>
            </div>
          </div>
        </div> */}
      </div>
      <div className="infoCardsContainer">
        <div className="info-icon-container-wrapper">
          <InfoCards
            icon={countIcn}
            info={t("cwpm")}
            value={`${activeResult.cwpm ?? 0}`}
          />
          <div className="info-icon-container">
            <div className="cwpm-info">
              <div className="cwpm-info-heading">{t("cwpm_info_heading")}</div>
              <ul className="cwpm-info-list">
                <li>{tSubject("cwpm_info_range_1")}</li>
                <li>{tSubject("cwpm_info_range_2")}</li>
                <li>{tSubject("cwpm_info_range_3")}</li>
                <li>{tSubject("cwpm_info_range_4")}</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="info-icon-container-wrapper">
          <InfoCards
            icon={correctPercentageIcn}
            info={t("accuracy")}
            value={`${formattedWordsPercentage}%`}
          />
          <div className="info-icon-container">
            <div className="cwpm-info">{t("accuracy_info")}</div>
          </div>
        </div>
        <div className="info-icon-container-wrapper">
          <InfoCards
            icon={PhonemeAccuracyIcn}
            info={tSubject("phoneme_accuracy")}
            value={`${formattedPhonemePercentage}%`}
          />
          <div className="info-icon-container">
            <div className="cwpm-info">{t("phoneme_info")}</div>
          </div>
        </div>
      </div>
      {/* <div className="quicksight-card">
        <div className="quicksight-card-header">{t("quick_sight")}</div>
        <div className="quicksight-grid">
          <ResultCard
            heading={t("correct_words")}
            headingColor="#7EB66B"
            bgColor="rgba(126, 182, 107, 0.12)"
            value={activeResult.correct_words?.length || 0}
            image={correct}
            handleQuickSightCardOnClick={handleQuickSightCardOnClick}
          />
          <ResultCard
            heading={t("missed_words")}
            headingColor="#9A17C8"
            bgColor="rgba(154, 23, 200, 0.12)"
            value={activeResult.missed_words?.length || 0}
            image={missed}
            handleQuickSightCardOnClick={handleQuickSightCardOnClick}
          />
          <ResultCard
            heading={t("extra_words")}
            headingColor="#5FBCF0"
            bgColor="rgba(95, 188, 240, 0.12)"
            value={activeResult.extra_words?.length || 0}
            image={extra}
            handleQuickSightCardOnClick={handleQuickSightCardOnClick}
          />
          <ResultCard
            heading={t("incorrect_words")}
            headingColor="#E70000"
            bgColor="rgba(212, 43, 83, 0.12)"
            value={activeResult.incorrect_words?.length || 0}
            image={incorrect}
            handleQuickSightCardOnClick={handleQuickSightCardOnClick}
          />
        </div>
      </div> */}
      <div className="paragraph-wrapper-card">
        <div className="para-heading">{t("paragraph_recording")}</div>
        <div className="paragraph-card">
          {activeProcessedData?.qualityFailReason != null && (
            <AudioQualityWarningBanner
              failReason={activeProcessedData.qualityFailReason}
            />
          )}
          {activeProcessedData?.audio_presigned_url && (
            <div className="audio-player-wrapper">
              <div className="audio-player">
                <AudioPlayer
                  audioBlobUrl={activeProcessedData?.audio_presigned_url}
                  audioBlobRef={audioBlobRef}
                  setAudioBlobRef={setAudioBlobRef}
                  onPlayEventName="result_2a_audio_play_button"
                  onPauseEventName="result_2a_audio_pause_button"
                />
              </div>
            </div>
          )}
          <div className={`paragraph-para ${expandText ? "expanded" : ""}`}>
            {activeProcessedData?.paragraph}
          </div>
          {activeProcessedData?.paragraph && (
            <button
              type="button"
              className="paragraph-see-more"
              onClick={() => setExpandText(!expandText)}
            >
              {expandText ? t("see_less") : t("see_more")}
            </button>
          )}
        </div>
      </div>
      <div className="detailed-result-card">
        <div className="detailed-result-card-heading">
          {t("detailed_result")}
        </div>
        <div className="detailed-result-grid">
          {detailedResult.map((res) => (
            <div
              key={res.index}
              ref={res.index === scrollCardIndex ? scrollToRef : null}
            >
              <DetailedResultCard
                key={res.index}
                // index={res.index}
                heading={res.heading}
                subHeading={res.subHeading}
                bgColor={res.bgColor}
                icon={res.icon}
                innerBorderColor={res.innerBorderColor}
                words={res.words}
                // scrollCardIndex={scrollCardIndex}
              />
            </div>
          ))}
        </div>
      </div>
      <div
        className={`feedback-container ${isFeedbackDisabled ? "disabled" : ""}`}
      >
        <div className="feedback-container-info-wrapper">
          <div className="feedback-content-wrapper">
            <div
              className={`feedback-container-header ${isFeedbackDisabled ? "disabled" : ""}`}
            >
              {t("ai_result_feedback_heading")}
              <div className="feedback-container-header-info">
                {t("ai_result_feedback_info")}
              </div>
            </div>
            <div className="feedback-container-buttons-wrapper">
              <div
                className={`feedback-button ${feedbackValueSelected === "Yes" ? "selected" : ""} ${isFeedbackDisabled ? "disabled" : ""}`}
                onClick={() => handleFeedbackButtonClick("Yes")}
              >
                {t("feedback_yes")}
                {!isFeedbackDisabled && " 😃"}
              </div>
              <div
                className={`feedback-button ${feedbackValueSelected === "No" ? "selected" : ""} ${isFeedbackDisabled ? "disabled" : ""}`}
                onClick={() => handleFeedbackButtonClick("No")}
              >
                {t("feedback_no")}
                {!isFeedbackDisabled && " 🙁"}
              </div>
            </div>
          </div>
          <div className="feedback-image-wrapper">
            <img
              src={
                isFeedbackDisabled
                  ? feedbackWidgetDisabledIcn
                  : feedbackWidgetIcn
              }
              alt="Thumbs Up illustration"
            />
          </div>
        </div>
      </div>
      {feedbackDrawerOpen && (
        <div
          className="feedback-bottom-drawer-overlay"
          onClick={handleOverlayClick}
        >
          <div
            className="feedback-bottom-drawer"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="feedback-drawer-puller" />
            <div className="feedback-bottom-drawer-heading">
              {t("feedback_suggestion")}
            </div>
            <div className="feedback-bottom-drawer-subheading">
              {t("ai_result_feedback_heading")}
            </div>
            <div className="feedback-container-buttons-wrapper">
              <div
                className={`feedback-button ${feedbackValueSelected === "Yes" ? "selected" : ""}`}
                onClick={() => handleFeedbackButtonClick("Yes")}
              >
                {t("feedback_yes")}
                {"  😃"}
              </div>
              <div
                className={`feedback-button ${feedbackValueSelected === "No" ? "selected" : ""}`}
                onClick={() => handleFeedbackButtonClick("No")}
              >
                {t("feedback_no")}
                {" 🙁"}
              </div>
            </div>
            <div className="feedback-bottom-drawer-subheading">
              {t("feedback_bottom_drawer_subheading")}
            </div>
            <div className="feedback-bottom-drawer-visual-info">
              <div className="feedback-bottom-drawer-visual-info-sub">
                <img src={feedbackSpeakerIcn} alt="Speaker Aloud" />
                <div className="feedback-bottom-drawer-visual-info-text">
                  {t("ambient_noise")}
                </div>
              </div>
              <div className="feedback-bottom-drawer-visual-info-sub">
                <img src={feedbackUserIcn} alt="User Speaking" />
                <div className="feedback-bottom-drawer-visual-info-text">
                  {t("pronounciation_variation")}
                </div>
              </div>
              <div className="feedback-bottom-drawer-visual-info-sub">
                <img src={feedbackMicrophoneIcn} alt="Microphone Quality" />
                <div className="feedback-bottom-drawer-visual-info-text">
                  {t("microphone_quality")}
                </div>
              </div>
            </div>
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder={t("detail_your_opinion")}
              className="feedback-bottom-drawer-textarea"
            />
            <div
              className={`feedback-bottom-drawer-submit-button ${feedbackSubmitDisabled ? "disabled" : ""}`}
              onClick={() => {
                if (!feedbackSubmitDisabled) {
                  handleFeedbackSubmitButtonClick();
                }
              }}
            >
              {t("submit")}
            </div>
          </div>
        </div>
      )}
      {/* <button className="fab-scroll-top" onClick={scrollToTop}>
        <ArrowUpwardIcon fontSize="medium" />
      </button> */}
    </div>
  );
}

export default DemoResultsScreen;
